/**
 * Auto-match invoice line items to PO line items.
 *
 * Each invoice line is paired to at most one PO line (and vice versa) by a
 * combined score of description similarity + unit price + line total +
 * quantity agreement. Pairs below the confidence threshold are left for
 * the clerk to resolve manually.
 *
 * The algorithm is intentionally simple so the matcher can run per-render
 * without a backend round trip: Jaccard token overlap for descriptions,
 * small bonuses for exact numeric agreement. Short of a real training
 * loop this is the honest ceiling.
 */

import type { LineItem, POMatchLine } from '../types/invoice';

export interface PoMatchResult {
  invoiceIndex: number;
  poDetailKey: string;
  poLine: POMatchLine;
  score: number;
}

/** Lowercase, strip non-word characters, split on whitespace. */
function tokenize(s: string | null | undefined): string[] {
  if (!s) return [];
  return String(s)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 0);
}

/** |A ∩ B| / |A ∪ B|. 1.0 when both empty (treat as perfect agreement
 *  for degenerate descriptions, but the result still has to clear the
 *  overall score threshold to be used). */
function jaccardSimilarity(a: string[], b: string[]): number {
  if (a.length === 0 && b.length === 0) return 1;
  if (a.length === 0 || b.length === 0) return 0;
  const setA = new Set(a);
  const setB = new Set(b);
  let intersection = 0;
  setA.forEach((x) => {
    if (setB.has(x)) intersection++;
  });
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const PRICE_EPSILON = 0.01;
const QTY_EPSILON = 0.001;

/** Score a single invoice-line / PO-line pair. Range: roughly 0..1.9.
 *  Description similarity contributes up to 1.0; numeric agreements add
 *  small bonuses on top. */
export function scoreMatch(invoiceLine: Partial<LineItem>, poLine: POMatchLine): number {
  const descSim = jaccardSimilarity(
    tokenize(invoiceLine.description),
    tokenize(poLine.description),
  );

  const invUp = num(invoiceLine.unit_price);
  const poUp = num(poLine.unit_price);
  const upBonus = invUp > 0 && poUp > 0 && Math.abs(invUp - poUp) < PRICE_EPSILON ? 0.4 : 0;

  const invTotal = num(invoiceLine.total_price);
  const poTotal = num(poLine.total_price);
  const totalBonus = invTotal > 0 && poTotal > 0 && Math.abs(invTotal - poTotal) < PRICE_EPSILON ? 0.3 : 0;

  const invQty = num(invoiceLine.quantity);
  const poQty = num(poLine.quantity);
  const qtyBonus = invQty > 0 && poQty > 0 && Math.abs(invQty - poQty) < QTY_EPSILON ? 0.2 : 0;

  return descSim + upBonus + totalBonus + qtyBonus;
}

export interface AutoMatchOptions {
  /** Minimum combined score for a pair to be accepted. 0.7 is "pretty
   *  confident" with the current scoring weights. */
  minScore?: number;
  /** Only consider invoice lines whose current matched_po_detail_key is
   *  blank. True by default so re-running does not stomp on manual edits. */
  skipAlreadyMatched?: boolean;
}

/**
 * Greedy best-pair pairing: compute every (invoice, po) pair's score,
 * sort descending, take pairs that still have both sides free. Each PO
 * line can be matched to at most one invoice line and vice versa.
 */
export function autoMatchPoLines(
  invoiceLines: LineItem[],
  poLines: POMatchLine[],
  opts: AutoMatchOptions = {},
): PoMatchResult[] {
  const minScore = opts.minScore ?? 0.7;
  const skip = opts.skipAlreadyMatched !== false;

  const invoiceAlreadyMatched = new Set<number>();
  const poAlreadyMatched = new Set<string>();
  if (skip) {
    invoiceLines.forEach((l, i) => {
      if ((l.matched_po_detail_key || '').trim()) {
        invoiceAlreadyMatched.add(i);
        poAlreadyMatched.add((l.matched_po_detail_key || '').trim());
      }
    });
  }

  // Build and score every candidate pair.
  const pairs: Array<{ i: number; j: number; score: number }> = [];
  for (let i = 0; i < invoiceLines.length; i++) {
    if (invoiceAlreadyMatched.has(i)) continue;
    const inv = invoiceLines[i];
    for (let j = 0; j < poLines.length; j++) {
      const po = poLines[j];
      if (!po.detail_key) continue;
      if (poAlreadyMatched.has(po.detail_key)) continue;
      const s = scoreMatch(inv, po);
      if (s >= minScore) pairs.push({ i, j, score: s });
    }
  }

  pairs.sort((a, b) => b.score - a.score);

  const usedInvoice = new Set<number>();
  const usedPo = new Set<number>();
  const out: PoMatchResult[] = [];
  for (const p of pairs) {
    if (usedInvoice.has(p.i) || usedPo.has(p.j)) continue;
    usedInvoice.add(p.i);
    usedPo.add(p.j);
    out.push({
      invoiceIndex: p.i,
      poDetailKey: poLines[p.j].detail_key,
      poLine: poLines[p.j],
      score: p.score,
    });
  }
  return out;
}
